//
import { ApolloClient } from 'apollo-client'
import { InMemoryCache } from 'apollo-cache-inmemory'
import { HttpLink } from 'apollo-link-http'
import { onError } from 'apollo-link-error'
import getModel from './graphql/query/model.graphql'
import getLocalization from './graphql/query/getLocalization.graphql'
import getFullLocalization from './graphql/query/getFullLocalization.graphql'
import getComplexLocalization from './graphql/query/getComplexLocalization.graphql'
import appVersion from './graphql/query/appVersion.graphql'
import addAnalytics from './graphql/mutations/analytics/addAnalytics.graphql'
import gql from 'graphql-tag'
import auth from '@it-enterprise/jwtauthentication/dist/jwtauthentication.umd.min.js'
import Vue from 'vue'
import { processingApi } from '@/api/processingApi'
import { notificationApi } from '@/api/notificationApi'
import { i18n } from '../plugins/i18n'
import { deepCopy, getApplicationName } from '@/tools'

// show erros to user by notifications
let silentErrors = false

const errorLink = onError(({ graphQLErrors, networkError, response }) => {
  if (networkError && networkError.statusCode === 401) {
    auth.clearTokens()
    Vue.$routerCore.GoToSignIn()
  }
  if (graphQLErrors) {
    if (!silentErrors) {
      // TODO: Обработать ошибку
      if (Array.isArray(graphQLErrors) && graphQLErrors.some(x => x.message.includes('NOT_ALLOWED_SCHEMA'))) {
        let errMsg = graphQLErrors.find(x => x.message.includes('NOT_ALLOWED_SCHEMA')).message.substr(18)
        notificationApi.showError({ 
          text: errMsg,
          showDetails: false
        })
        return errMsg
      }
      notificationApi.showError({ 
        text: i18n.t('messages.dataAccessError'), 
        dateTime: response?.dateTime, 
        requestId: response?.requestId 
      })
      processingApi.hideAll()
    }

    return graphQLErrors.message
  }
})

const getClient = async schema => {
  const token = await auth.getToken()
  const options = {
    // eslint-disable-next-line no-undef
    uri: appConfig.GraphQlUrl + 'api/graphql',
    credentials: 'include',
    headers: {
      Authorization: `Bearer ${token}`,
      // TODO: Убрать HRPORTAL
      schema: schema ?? 'HRPORTAL',
      'Accept-Language': i18n.locale, 
      'Application-Name': getApplicationName()
    }
  }
  const httpLink = new HttpLink(options)
  return new ApolloClient({
    cache: new InMemoryCache({
      addTypename: false
    }),
    link: errorLink.concat(httpLink)
  })
}

export { getClient }

function getOperationsFromModel() {
  if (!Vue.$authCore.store.getters.DataSources) {
    return []
  }
  return Vue.$authCore.store.getters.DataSources.map(x => {
    return { $ID: x.$ID, Query: x.Query, Mutations: x.Mutations, GraphQlSchema: x.GraphQlSchema }
  })
}

async function callModelOperation(options, args) {
  let ds = getOperationsFromModel().find(x => x.$ID == options.dataSourceId)
  const client = await getClient(ds.GraphQlSchema)
  if (!options.operationId) {
    return await client.query({
      query: gql`${ds.Query}`,
      variables: args
    })
  }

  let mutation = ds.Mutations.find(x => x.$ID == options.operationId).Value
  return await client.mutate({
    mutation: gql`${mutation}`,
    variables: args
  })
}

export class GraphqQlApi {
  static async addAnalytics(data) {
    try {
      const client = await getClient('WebAppModeler')
      return await client.mutate({
        mutation: gql`${addAnalytics}`,
        variables: { analytics: data }
      })
    } catch (e) {
      throw new Error(e.message)
    }
    
  }

  static async callOperation(options, args) {
    try {
      return await callModelOperation(options, deepCopy(args))
    } catch (e) {
      throw new Error(e.message)
    }
  }

  // get model 
  static async getModelFromGqlAsync(appName, isSystem) {
    try {
      const client = await getClient('WebAppModeler')
      return await client.query({
        query: gql`${getModel}`,
        variables: { appName, isSystem }
      })
    } catch (e) {
      throw new Error(e.message)
    }
  }

  /**
   * Get system localization
   * @param {*} params 
   * @returns 
   */
  static async getLocalizationAsync(params) {
    try {
      const client = await getClient('WebAppModeler')
      return await client.query({
        query: gql`${getLocalization}`,
        variables: params
      })
    } catch (e) {
      throw new Error(e.message)
    }
  }

  /**
   * Get system and users localization
   * @param {*} params 
   * @returns 
   */
  static async getComplexLocalizationAsync(params) {
    let response = null

    try {
      silentErrors = true
      const client = await getClient('WebAppModeler')
      response = await client.query({
        query: gql`${getComplexLocalization}`,
        variables: params
      })
    } catch (e) {
      throw new Error(e.message)
    } finally {
      silentErrors = false
    }

    return response
  }

  /**
   * Get system and users localization
   * @param {*} params 
   * @returns 
   */
  static async getFullLocalizationAsync(params) {
    let response = null

    try {
      silentErrors = true
      const client = await getClient('WebAppModeler')
      response = await client.query({
        query: gql`${getFullLocalization}`,
        variables: params
      })
    } catch (e) {
      throw new Error(e.message)
    } finally {
      silentErrors = false
    }

    return response
  }

  static async getApplicationVersionAsync(appName) {
    try {
      const client = await getClient('WebAppModeler')
      const response = await client.query({
        query: gql`${appVersion}`,
        variables: { appName }
      })
      return response
    } catch (e) {
      console.error(e.message)
    }
  }
}
